import feathers from '../feathers';
import { useAppSelector, useAppDispatch } from '../redux/store';
import { updateUser } from '../redux/slices/userSlice';
import { DBUserThunks, userSelectOneObjectByQuery } from '../redux/thunks/DBServiceThunks';

/**
 * Service to handle user language preference updates
 */
class UserLanguageService {
  /**
   * Update user's language preference in the database
   */
  async updateUserLanguage(userId: string, language: string): Promise<void> {
    try {
      // Update user in the database
      await feathers.service('users').patch(userId, { language });

      console.log(`Updated user ${userId} language preference to ${language}`);
    } catch (error) {
      console.error('Failed to update user language preference:', error);
      throw error;
    }
  }

  /**
   * Get user's saved language preference
   */
  async getUserLanguage(userId: string): Promise<string | null> {
    try {
      const user = await feathers.service('users').get(userId);
      return user.language || null;
    } catch (error) {
      console.error('Failed to get user language preference:', error);
      return null;
    }
  }
}

export const userLanguageService = new UserLanguageService();

/**
 * Hook to handle language changes with database persistence
 */
export const useUserLanguage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.UserReducer.user);
  const dbUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, { _id: user?._id }));

  const updateLanguagePreference = async (language: string) => {
    if (!user?._id) {
      console.warn('Cannot update language preference: user not authenticated');
      return;
    }

    try {
      // Update language preference in database
      await userLanguageService.updateUserLanguage(user._id, language);

      // Update local user state
      dispatch(updateUser({ language }));

    } catch (error) {
      console.error('Failed to update language preference:', error);
      // You might want to show a toast notification here
    }
  };

  const getUserLanguage = (): string => {
    // Priority: dbUser.language > user.language > 'en'
    const result = dbUser?.language || user?.language || 'en';

    return result;
  };

  return {
    updateLanguagePreference,
    getUserLanguage,
    currentLanguage: getUserLanguage()
  };
}; 